import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Compass, Star } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { setSeoMetadata } from '../../lib/seo';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import { fetchJsonCached } from '../../lib/settingsCache';
import { useSiteFeatureFlags } from '../../hooks/useSiteFeatureFlags';
import { ROUTES } from '../../data/stjSite';
import { categoryUrl } from '../../lib/activityUrls';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * All Tours & Charters - the landing page for every activity category.
 * This is also where the header BOOK DIRECT button sends people.
 */
const AllToursChartersPage = () => {
  const { activity_marketplace_enabled, _loaded } = useSiteFeatureFlags();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSeoMetadata({
      title: 'All Tours & Charters | Book Direct | St John USVI',
      description:
        'Browse every tour, charter and excursion category on St John, USVI - boat charters, kayaking, sailing, hiking, BVI trips, fishing, diving and more. Book direct with local operators.',
      keywords:
        'st john tours, st john charters, book direct st john, boat charters usvi, excursions st john, all tours and charters',
      canonicalPath: ROUTES.allToursCharters,
      ogType: 'website',
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchJsonCached(`${API_URL}/api/public/tours-charters/categories?top_level_only=true`);
        if (!cancelled) setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!cancelled) setCategories([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-28 lg:pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4">
          <header className="text-center mb-4">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold italic uppercase text-slate-900 tracking-tight">
              All Tours &amp; Charters
            </h1>
            <p className="font-script text-3xl text-cyan-500 mt-3">
              Book Direct with St John&rsquo;s Best Operators
            </p>
          </header>

          <p className="text-center text-slate-600 max-w-3xl mx-auto mb-12">
            Pick a category to see every available trip. Our concierge service is free &mdash; when you
            book direct you deal straight with the local operator.
          </p>

          {loading || !_loaded ? (
            <div className="flex justify-center py-16" data-testid="all-tours-loading">
              <div className="w-10 h-10 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !activity_marketplace_enabled ? (
            <p className="text-center text-slate-500 py-16" data-testid="all-tours-disabled">
              Our tours &amp; charters directory is being updated. Please check back soon.
            </p>
          ) : categories.length === 0 ? (
            <div className="text-center py-16" data-testid="all-tours-empty">
              <Compass className="w-12 h-12 mx-auto text-slate-300" />
              <p className="mt-4 text-slate-500">Categories are being added. Check back soon!</p>
              <Link to={ROUTES.bookYourTrip}
                className="mt-6 inline-block px-6 py-3 bg-red-600 text-white font-bold rounded hover:bg-red-700 transition-colors">
                Contact our concierge
              </Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="all-tours-grid">
                {categories.map((cat) => (
                  <article key={cat.id} data-testid={`tour-category-${cat.slug}`}>
                    <Link
                      to={categoryUrl(cat)}
                      className="group relative block aspect-[4/3] rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all"
                    >
                      {cat.image_url ? (
                        <img
                          src={resolveMediaUrl(cat.image_url)}
                          alt={cat.name}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <span className="absolute inset-0 bg-gradient-to-br from-sky-500 to-blue-700" />
                      )}
                      <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                      {cat.is_favorite && (
                        <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-amber-400/95 px-2.5 py-1 text-[11px] font-bold text-amber-950 shadow"
                          data-testid={`tour-category-star-${cat.slug}`}>
                          <Star className="w-3 h-3 fill-current" /> Featured
                        </span>
                      )}

                      <div className="absolute inset-x-0 bottom-0 p-4 text-center">
                        <h2 className="text-white text-base sm:text-lg lg:text-xl font-bold uppercase tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.85)]">
                          {cat.name}
                        </h2>
                        {(cat.total_activity_count ?? cat.activity_count) > 0 && (
                          <p className="mt-1 text-white/85 text-xs font-semibold">
                            {cat.total_activity_count ?? cat.activity_count}{' '}
                            {(cat.total_activity_count ?? cat.activity_count) === 1 ? 'trip' : 'trips'}
                          </p>
                        )}
                      </div>
                    </Link>
                    {cat.description && (
                      <p className="mt-3 text-sm text-slate-600 leading-relaxed">{cat.description}</p>
                    )}
                  </article>
                ))}
              </div>

              <div className="mt-14 text-center">
                <p className="text-slate-700 font-semibold">Not sure where to start?</p>
                <div className="mt-4 flex flex-wrap justify-center gap-4">
                  <Link to={ROUTES.bookYourTrip}
                    className="px-8 py-4 bg-red-600 text-white font-bold rounded hover:bg-red-700 transition-colors shadow-lg"
                    data-testid="all-tours-book-cta">
                    Book Your Trip
                  </Link>
                  <Link to={ROUTES.activitiesCompanies}
                    className="px-8 py-4 bg-white text-blue-700 font-bold rounded border-2 border-blue-600 hover:bg-blue-50 transition-colors inline-flex items-center gap-2"
                    data-testid="all-tours-companies-cta">
                    Browse by Charter Company <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AllToursChartersPage;
